if (!customElements.get('custom-collection-grid')) {
  const SORTS = ['featured', 'low-high', 'high-low', 'newest', 'oldest', 'in-stock'];

  customElements.define(
    'custom-collection-grid',
    class CustomCollectionGrid extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-cx-grid]');
        this.empty = this.querySelector('[data-cx-empty]');
        if (!this.grid) return;
        this.defaultView = this.dataset.defaultView || 'column';
        this.readState();

        this.addEventListener('click', (event) => this.onClick(event));
        this.onDocumentClick = (event) => {
          if (!event.target.closest('[data-cx-dropdown]')) this.closeDropdowns();
        };
        this.onKeydown = (event) => {
          if (event.key === 'Escape') this.closeDropdowns(true);
        };
        this.onPopState = () => {
          this.readState();
          this.apply();
        };
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('popstate', this.onPopState);

        this.apply();
        this.loadRemainingPages();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('popstate', this.onPopState);
      }

      // ---------- State (kept in the URL like the reference: ?sort=&layout=&tags=) ----------
      // The reference calls the layout param `view`, but Shopify reserves `view` for alternate templates.
      readState() {
        const params = new URLSearchParams(window.location.search);
        const sort = params.get('sort');
        const view = params.get('layout');
        this.sort = SORTS.includes(sort) ? sort : 'featured';
        this.view = view === 'grid' || view === 'column' ? view : this.defaultView;
        this.tags = (params.get('tags') || '')
          .split(',')
          .map((tag) => tag.trim().toLowerCase())
          .filter(Boolean);
      }

      writeState() {
        const params = new URLSearchParams(window.location.search);
        params.set('sort', this.sort);
        params.set('layout', this.view);
        if (this.tags.length) params.set('tags', this.tags.join(','));
        else params.delete('tags');
        params.delete('page');
        const query = params.toString().replace(/%2C/g, ',');
        window.history.pushState({}, '', `${window.location.pathname}?${query}`);
      }

      // ---------- Events ----------
      onClick(event) {
        const trigger = event.target.closest('.cx-dropdown__trigger');
        if (trigger) {
          const dropdown = trigger.closest('[data-cx-dropdown]');
          const open = trigger.getAttribute('aria-expanded') !== 'true';
          this.closeDropdowns();
          if (open) this.setDropdown(dropdown, true);
          return;
        }

        const mobileToggle = event.target.closest('[data-cx-mobile-toggle]');
        if (mobileToggle) {
          const open = mobileToggle.getAttribute('aria-expanded') !== 'true';
          mobileToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
          document.getElementById(mobileToggle.getAttribute('aria-controls'))?.classList.toggle('is-open', open);
          return;
        }

        const sortButton = event.target.closest('[data-cx-sort]');
        if (sortButton) {
          this.sort = sortButton.dataset.cxSort;
          this.closeDropdowns();
          return this.commit();
        }

        const viewButton = event.target.closest('[data-cx-view]');
        if (viewButton) {
          this.view = viewButton.dataset.cxView;
          return this.commit();
        }

        const clearButton = event.target.closest('[data-cx-tags-clear]');
        if (clearButton) {
          const groupTags = this.groupTags(clearButton.dataset.cxTagsClear);
          this.tags = this.tags.filter((tag) => !groupTags.includes(tag));
          this.closeDropdowns();
          return this.commit();
        }

        const tagButton = event.target.closest('[data-cx-tag]');
        if (tagButton) {
          const tag = tagButton.dataset.cxTag.toLowerCase();
          this.tags = this.tags.includes(tag) ? this.tags.filter((item) => item !== tag) : [...this.tags, tag];
          this.closeDropdowns();
          return this.commit();
        }
      }

      commit() {
        this.writeState();
        this.apply();
      }

      setDropdown(dropdown, open) {
        const trigger = dropdown.querySelector('.cx-dropdown__trigger');
        const panel = dropdown.querySelector('.cx-dropdown__panel');
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        panel.hidden = !open;
      }

      closeDropdowns(restoreFocus = false) {
        this.querySelectorAll('[data-cx-dropdown]').forEach((dropdown) => {
          const trigger = dropdown.querySelector('.cx-dropdown__trigger');
          const wasOpen = trigger.getAttribute('aria-expanded') === 'true';
          this.setDropdown(dropdown, false);
          if (wasOpen && restoreFocus) trigger.focus();
        });
      }

      groupTags(groupId) {
        return Array.from(this.querySelectorAll(`[data-cx-tag-group="${groupId}"]`)).map((button) =>
          button.dataset.cxTag.toLowerCase(),
        );
      }

      // ---------- Rendering ----------
      apply() {
        const condensed = this.view === 'grid' || this.tags.length > 0 || this.sort !== 'featured';
        this.grid.classList.toggle('cx-grid--grid', this.view === 'grid');
        this.grid.classList.toggle('cx-grid--column', this.view !== 'grid');
        this.grid.classList.toggle('is-condensed', condensed);

        const items = Array.from(this.grid.children).filter((child) =>
          child.matches('.cx-grid__item, .cx-grid__extra, .cx-grid__feature'),
        );
        const key = (node) => {
          if (node.matches('.cx-grid__extra')) return parseFloat(node.dataset.position);
          const tile = node.querySelector('[data-cx-product]');
          const position = tile ? parseFloat(tile.dataset.position) : 0;
          return node.matches('.cx-grid__feature') ? position - 0.1 : position;
        };
        const number = (node, name) => parseFloat(node.querySelector('[data-cx-product]')?.dataset[name] || '0');
        const byPosition = (a, b) => key(a) - key(b);

        let ordered = [...items].sort(byPosition);
        if (this.sort === 'low-high') ordered = ordered.sort((a, b) => number(a, 'price') - number(b, 'price'));
        if (this.sort === 'high-low')
          ordered = ordered.sort((a, b) => number(a, 'price') - number(b, 'price')).reverse();
        if (this.sort === 'oldest') ordered = ordered.sort((a, b) => number(a, 'created') - number(b, 'created'));
        if (this.sort === 'newest')
          ordered = ordered.sort((a, b) => number(a, 'created') - number(b, 'created')).reverse();
        if (this.sort === 'in-stock') ordered = ordered.sort((a, b) => number(b, 'inStock') - number(a, 'inStock'));

        // Tag filter: a product must match at least one selected tag in every group that has a selection.
        const groups = Array.from(
          new Set(Array.from(this.querySelectorAll('[data-cx-tag-group]')).map((b) => b.dataset.cxTagGroup)),
        )
          .map((id) => this.groupTags(id).filter((tag) => this.tags.includes(tag)))
          .filter((selected) => selected.length);
        let visible = 0;
        ordered.forEach((node) => {
          const tile = node.matches('.cx-grid__item') ? node.querySelector('[data-cx-product]') : null;
          let show = true;
          if (tile && groups.length) {
            const tags = tile.dataset.tags || '';
            show = tags.length > 2 && groups.every((selected) => selected.some((tag) => tags.includes(`|${tag}|`)));
          }
          if (node.matches('.cx-grid__item')) {
            node.hidden = !show;
            if (show) visible++;
          }
          this.grid.appendChild(node);
        });
        if (this.empty) this.empty.hidden = visible > 0 || items.length === 0;

        this.syncControls();
      }

      syncControls() {
        this.querySelectorAll('[data-cx-sort]').forEach((b) =>
          b.setAttribute('aria-pressed', b.dataset.cxSort === this.sort ? 'true' : 'false'),
        );
        this.querySelectorAll('[data-cx-view]').forEach((b) =>
          b.setAttribute('aria-pressed', b.dataset.cxView === this.view ? 'true' : 'false'),
        );
        this.querySelectorAll('[data-cx-tag]').forEach((b) =>
          b.setAttribute('aria-pressed', this.tags.includes(b.dataset.cxTag.toLowerCase()) ? 'true' : 'false'),
        );
        this.querySelectorAll('[data-cx-tags-clear]').forEach((b) => {
          const active = this.groupTags(b.dataset.cxTagsClear).some((tag) => this.tags.includes(tag));
          b.setAttribute('aria-pressed', active ? 'false' : 'true');
        });
        const sortButton = this.querySelector(`[data-cx-sort="${this.sort}"][data-cx-label]`);
        const sortSummary = this.querySelector('[data-cx-sort-summary]');
        if (sortButton && sortSummary) sortSummary.textContent = sortButton.dataset.cxLabel;
        this.querySelectorAll('[data-cx-tag-summary]').forEach((summary) => {
          const selected = this.groupTags(summary.dataset.cxTagSummary).filter((tag) => this.tags.includes(tag));
          const allLabel = this.querySelector(
            `[data-cx-tags-clear="${summary.dataset.cxTagSummary}"] + .cx-radio__label`,
          );
          summary.textContent = selected.length ? selected.join(', ') : allLabel?.textContent.trim() || 'All';
        });
      }

      // ---------- Load every page of the collection, like the reference ----------
      async loadRemainingPages() {
        const pages = parseInt(this.grid.dataset.cxPages || '1', 10);
        const current = parseInt(this.grid.dataset.cxPage || '1', 10);
        if (pages <= 1 || current !== 1) return;
        const sectionId = this.dataset.sectionId;
        for (let page = 2; page <= pages; page++) {
          try {
            // Keep Shopify's own params (such as an alternate template `view`), drop the grid's state.
            const params = new URLSearchParams(window.location.search);
            ['sort', 'layout', 'tags'].forEach((name) => params.delete(name));
            params.set('page', page);
            params.set('section_id', sectionId);
            const url = `${window.location.pathname}?${params.toString()}`;
            const html = await (await fetch(url)).text();
            const grid = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cx-grid]');
            if (!grid) break;
            Array.from(grid.children).forEach((child) => {
              if (child.matches('.cx-grid__item, .cx-grid__extra, .cx-grid__feature')) this.grid.appendChild(child);
            });
          } catch (error) {
            console.error(error);
            break;
          }
        }
        this.apply();
      }
    },
  );
}
